import { GitBranchIcon } from '@primer/octicons-react'
import { Cross2Icon } from '@radix-ui/react-icons'
import { ColumnDef, getCoreRowModel, Row, useReactTable } from '@tanstack/react-table'
import classNames from 'classnames'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { SuggestedSeverity } from '../components/suggested-severity.tsx'
import { Pagination, Table } from '../components/table/table'
import { mappedColors, typography } from '../main.css.ts'
import { Finding, PaginatedFindings } from '../utils/api-client/api-schemas.ts'
import { getEnvironmentData } from '../utils/hooks/get-environment-data.ts'
import { SeverityBadge } from './badges/severity-badge.tsx'
import { DefaultButton } from './buttons/default-button.tsx'
import { GhostButton } from './buttons/ghost-button.tsx'
import * as styles from './findings-table.css'
import WarningIcon from './icons/warning-icon-filled.svg?react'
import { InProgressPulse } from './in-progress-pulse.tsx'
import { Tooltip, TooltipWithHover } from './tooltip'

export interface FindingsTableProps {
  paginatedFindings?: PaginatedFindings
  pagination?: Pagination
  handlePaginationChange: (pagination: Pagination) => void
  selectedFindingId?: string
  handleSetSelectedFindingIdForFindingTriageDrawer: (findingId: string | undefined) => void
  handleSetSelectedFindingIdForFindingFixDrawer: (findingId: string | undefined) => void
}
export const FindingsTable: React.FC<FindingsTableProps> = ({
  paginatedFindings,
  pagination,
  handlePaginationChange,
  selectedFindingId,
  handleSetSelectedFindingIdForFindingTriageDrawer,
  handleSetSelectedFindingIdForFindingFixDrawer,
}) => {
  const environmentData = getEnvironmentData()

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'severity',
          meta: { width: '5%' },
          header: () => <SeverityHeader />,
          cell: ({ row }) => <SeverityCell row={row} onSelect={handleSetSelectedFindingIdForFindingTriageDrawer} />,
        },
        {
          accessorKey: 'title',
          meta: { width: '26%' },
          header: () => <TitleHeader />,
          cell: ({ row }) => <TitleCell row={row} onSelect={handleSetSelectedFindingIdForFindingTriageDrawer} />,
        },
        {
          accessorKey: 'suggestedStatus',
          meta: { width: '13%' },
          header: () => <SuggestedStatusHeader environmentData={environmentData} />,
          cell: ({ row }) => <SuggestedStatusCell row={row} onSelect={handleSetSelectedFindingIdForFindingTriageDrawer} />,
        },
        {
          accessorKey: 'severityUpdate',
          meta: { width: '13%' },
          header: () => <SeverityUpdateHeader environmentData={environmentData} />,
          cell: ({ row }) => <SeverityUpdateCell row={row} onSelect={handleSetSelectedFindingIdForFindingTriageDrawer} />,
        },
        {
          accessorKey: 'summary',
          meta: { width: '16%' },
          header: () => <TriageSummaryHeader environmentData={environmentData} />,
          cell: ({ row }) => <TriageSummaryCell row={row} onSelect={handleSetSelectedFindingIdForFindingTriageDrawer} />,
        },
        {
          accessorKey: 'fix',
          meta: { width: '14%' },
          header: () => <FixHeader />,
          cell: ({ row }) => (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <FixCell row={row} onSelect={handleSetSelectedFindingIdForFindingFixDrawer} />
            </div>
          ),
        },
      ] as ColumnDef<Finding>[],
    [handleSetSelectedFindingIdForFindingTriageDrawer, handleSetSelectedFindingIdForFindingFixDrawer]
  )

  const rowSelection = useMemo(() => {
    if (!selectedFindingId) {
      return {}
    }

    return {
      [selectedFindingId]: true,
    }
  }, [selectedFindingId])

  const table = useReactTable<Finding>({
    data: paginatedFindings?._embedded?.items ?? [],
    getRowId: finding => finding.id,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
  })

  return (
    <>
      <Table
        table={table}
        isLoading={paginatedFindings === undefined}
        pagination={pagination}
        handlePaginationChange={handlePaginationChange}
        tableLabel="Findings"
        rowCount={paginatedFindings?.total}
        getRowClassName={row => {
          const isTriaged = row.original._embedded?.triage?.result === 'triaged'
          const className = classNames({
            [styles.selectableRow]: isTriaged,
          })
          return className
        }}
      />
    </>
  )
}

interface SelectableFindingCellProps {
  children: React.ReactNode
  handleClick: () => void
  isSelectable?: boolean
}
const SelectableFindingCell: React.FC<SelectableFindingCellProps> = ({ children, handleClick, isSelectable = true }) => (
  <div
    onClick={isSelectable ? handleClick : undefined}
    data-testid="selectable-finding-cell"
    style={isSelectable ? { cursor: 'pointer' } : undefined}
  >
    {children}
  </div>
)

interface FindingWarningTooltipProps {
  triage: NonNullable<NonNullable<Finding['_embedded']>['triage']>
}
const FindingWarningTooltip: React.FC<FindingWarningTooltipProps> = ({ triage }) => (
  <TooltipWithHover
    trigger={
      <span onClick={e => e.stopPropagation()}>
        <WarningIcon className={styles.warningIcon} aria-label="Warning: This finding was not triaged. Click to view details." />
      </span>
    }
    side="bottom"
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        color: mappedColors.neutral.neutral10,
      }}
    >
      <p className={typography.smallBold} style={{ margin: 0 }}>
        {match(triage.result)
          .with('rule_not_supported', () => 'This finding was not triaged')
          .otherwise(() => 'Error')}
      </p>
      <p className={typography.smallItalic} style={{ margin: 0 }}>
        {match(triage.result)
          .with('rule_not_supported', () => "Couldn't Analyze (Rule Not Supported Yet)")
          .otherwise(() => triage.summary || 'No summary available')}
      </p>
    </div>
  </TooltipWithHover>
)

export const getSuggestedStatusLabel = (status: NonNullable<Finding['_embedded']['triage']>['suggested_status']) => {
  return match(status)
    .with('wont_fix', () => "Won't fix")
    .with('false_positive', () => 'False positive')
    .with('true_positive', () => 'True positive')
    .with('suspicious', () => 'Suspicious')
    .with(null, () => '-')
    .exhaustive()
}

interface HeaderProps {
  environmentData?: any
}

const SeverityHeader: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <span className={styles.header} style={{ margin: 0, marginRight: '8px', padding: 0, width: 'fit-content' }}>
      SEVERITY
    </span>
  </div>
)

const TitleHeader: React.FC = () => (
  <span className={styles.header} style={{ justifyContent: 'flex-start' }}>
    FINDING
  </span>
)

const SuggestedStatusHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} alt={environmentData.logoIconAlt} height={16} style={{ marginRight: '8px' }} />
    <span
      className={styles.header}
      style={{
        margin: 0,
        marginRight: '8px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
    >
      CLASSIFICATION
    </span>
    <Tooltip side="bottom" width={422}>
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            justifyContent: 'space-between',
          }}
        >
          <img src={environmentData.logoIconHref} alt={environmentData.logoIconAlt} height={24} />
          <span className={typography.leadBold}>Triage</span>
        </div>
        <div>
          <p className={typography.smallBold} style={{ margin: 0 }}>
            True positive
          </p>
          <p className={typography.smallItalic} style={{ margin: 0 }}>
            Pixee can validate the finding as a legitimate issue
          </p>
        </div>
        <div style={{ marginTop: '8px' }}>
          <p className={typography.smallBold} style={{ margin: 0 }}>
            False positive
          </p>
          <p className={typography.smallItalic} style={{ margin: 0 }}>
            Pixee can confidently dismiss this finding as a false positive, though we may still offer a fix to satisfy the tool
          </p>
        </div>
        <div style={{ marginTop: '8px' }}>
          <p className={typography.smallBold} style={{ margin: 0 }}>
            Suspicious
          </p>
          <p className={typography.smallItalic} style={{ margin: 0 }}>
            Pixee suspects a true positive, given the absence of typical false positive conditions or security controls
          </p>
        </div>
        <div style={{ marginTop: '8px' }}>
          <p className={typography.smallBold} style={{ margin: 0 }}>
            Won't fix
          </p>
          <p className={typography.smallItalic} style={{ margin: 0 }}>
            The finding is considered negligible, in that the vast majority of teams would choose to accept any potential risk
          </p>
        </div>
        <div style={{ marginTop: '8px' }}>
          <p className={typography.smallBold} style={{ margin: 0 }}>
            No analysis
          </p>
          <p className={typography.smallItalic} style={{ margin: 0 }}>
            Pixee was not able to triage the finding
          </p>
        </div>
        <div style={{ marginTop: '8px' }}>
          <p className={typography.smallBold} style={{ margin: 0 }}>
            Null
          </p>
          <p className={typography.smallItalic} style={{ margin: 0 }}>
            Not classifiable by Pixee, though there may be analysis and/or a fix
          </p>
        </div>
      </div>
    </Tooltip>
  </div>
)

const SeverityUpdateHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} alt={environmentData.logoIconAlt} height={16} style={{ marginRight: '8px' }} />
    <span className={styles.header} style={{ margin: 0, marginRight: '8px', padding: 0, width: 'fit-content' }}>
      SEVERITY
    </span>
    <Tooltip side="bottom">
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            justifyContent: 'space-between',
          }}
        >
          <img src={environmentData.logoIconHref} alt={environmentData.logoIconAlt} height={24} />
          <span className={typography.leadBold}>Triage</span>
        </div>
        <p className={typography.small} style={{ margin: 0 }}>
          Based on triage analysis, Pixee may be able to suggest the severity is actually higher or lower than the tool initially reported.
        </p>
      </div>
    </Tooltip>
  </div>
)

const TriageSummaryHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
    <img src={environmentData.logoIconHref} alt={environmentData.logoIconAlt} height={16} style={{ marginRight: '8px' }} />
    <span className={styles.header} style={{ margin: 0, padding: 0, width: 'fit-content' }}>
      ANALYSIS
    </span>
  </div>
)

const FixHeader: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <span className={styles.header} style={{ margin: 0, padding: 0, width: 'fit-content' }}>
      FIX
    </span>
  </div>
)

interface CellProps {
  row: Row<Finding>
  onSelect: (id: string) => void
}

const SeverityCell: React.FC<CellProps> = ({ row, onSelect }) => {
  const isTriaged = row.original._embedded?.triage?.result === 'triaged'
  return (
    <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <SeverityBadge severity={row.original.severity} />
      </div>
    </SelectableFindingCell>
  )
}

const TitleCell: React.FC<CellProps> = ({ row, onSelect }) => {
  const isTriaged = row.original._embedded?.triage?.result === 'triaged'
  return (
    <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
      <div className={styles.findingTitle}>
        {row.original.title && row.original.title.length > 52 ? row.original.title.slice(0, 49) + '...' : (row.original.title ?? 'No title')}
        {row.original._embedded?.triage && row.original._embedded.triage.result !== 'triaged' && (
          <FindingWarningTooltip triage={row.original._embedded.triage} />
        )}
      </div>
      <p className={styles.findingRule}>{row.original.rule}</p>
    </SelectableFindingCell>
  )
}

const SuggestedStatusCell: React.FC<CellProps> = ({ row, onSelect }) => {
  const isTriaged = row.original._embedded?.triage?.result === 'triaged'
  if (row.original._embedded?.triage && row.original._embedded.triage.suggested_status) {
    return (
      <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
        <span className={styles.suggestedStatusCell[row.original._embedded.triage.suggested_status]}>
          {getSuggestedStatusLabel(row.original._embedded.triage.suggested_status)}
        </span>
      </SelectableFindingCell>
    )
  }
  return (
    <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
      <span className={styles.suggestedStatusCell.null}>-</span>
    </SelectableFindingCell>
  )
}

const SeverityUpdateCell: React.FC<CellProps> = ({ row, onSelect }) => {
  const isTriaged = row.original._embedded?.triage?.result === 'triaged'
  return (
    <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <SuggestedSeverity severity={row.original.severity} suggestedSeverity={row.original._embedded?.triage?.suggested_severity ?? null} />
      </div>
    </SelectableFindingCell>
  )
}

const TriageSummaryCell: React.FC<CellProps> = ({ row, onSelect }) => {
  const isTriaged = row.original._embedded?.triage?.result === 'triaged'
  return row.original._embedded?.triage?.summary ? (
    <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
      <span className={styles.analysisCell}>
        {row.original._embedded.triage.summary.length > 30
          ? row.original._embedded.triage.summary.slice(0, 30) + '...'
          : row.original._embedded.triage.summary}
      </span>
    </SelectableFindingCell>
  ) : (
    <SelectableFindingCell handleClick={() => onSelect(row.original.id)} isSelectable={isTriaged}>
      <span className={styles.placeholderCell}>-</span>
    </SelectableFindingCell>
  )
}

const FixCell: React.FC<CellProps> = ({ row, onSelect }) => {
  const latestPatchState = row.original._embedded?.['latest-patch']?.state

  if (latestPatchState) {
    return match(latestPatchState)
      .with('in_progress', () => (
        <GhostButton type="secondary" className={styles.fixCellButton} onClick={() => onSelect(row.original.id)} label="View">
          View
          <span role="status" aria-label="Patch in progress" className={styles.patchStatus}>
            <InProgressPulse className={styles.patchInProgressIcon} />
          </span>
        </GhostButton>
      ))
      .with('completed_success', () => (
        <GhostButton type="secondary" className={styles.fixCellButton} onClick={() => onSelect(row.original.id)} label="View">
          View
          <span role="status" aria-label="Patch completed" className={styles.patchStatus}>
            <GitBranchIcon className={styles.patchCompletedIcon} />
          </span>
        </GhostButton>
      ))
      .with('completed_failed', () => (
        <GhostButton type="secondary" className={styles.fixCellButton} onClick={() => onSelect(row.original.id)} label="View">
          View
          <span role="status" aria-label="Patch completed with errors" className={styles.patchStatus}>
            <Cross2Icon className={styles.patchCompletedWithErrorsIcon} />
          </span>
        </GhostButton>
      ))
      .exhaustive()
  }

  const fixesTotal = row.original._embedded?.fixes?.total

  if (fixesTotal && fixesTotal > 0) {
    return (
      <DefaultButton buttonType={'secondary'} size="small" onClick={() => onSelect(row.original.id)}>
        Preview
      </DefaultButton>
    )
  }

  return (
    <div className={styles.placeholderCell} style={{ justifyContent: 'center' }}>
      <span style={{ display: 'flex', justifyContent: 'center' }}>-</span>
    </div>
  )
}
